import { ElementType, useEffect, useState } from "react"
import CopyToClipboard from "react-copy-to-clipboard"
import styled, { PolymorphicComponentProps } from "styled-components"
import { Typography, TypographyVariant } from "../../typography"
import { DeprecatedButton, DeprecatedButtonProps } from "../button"

interface LabelProps {
  $variant?: TypographyVariant
}

export const Label = styled(Typography).attrs<LabelProps>((props) => ({
  as: props.as ?? "span",
  $variant: props.$variant ?? "body-4-medium",
}))``

const Pre = styled(Typography).attrs<{ $variant?: TypographyVariant }>((props) => ({
  as: props.as ?? "pre",
  $variant: props.$variant ?? "body-4-regular",
}))`
  display: flex;
  justify-content: space-between;
  overflow: auto;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.grey.$200};
  box-shadow: ${({ theme }) => theme.shadows.light};
  border-radius: 0.5rem;
  margin: 0.25rem 0 1.25rem 0;
`

const Container = styled.div`
  width: 100%;
  max-width: 27.5rem;
`

const SuccessMessage = styled(Typography).attrs<{ $variant?: TypographyVariant }>((props) => ({
  as: props.as ?? "span",
  $variant: props.$variant ?? "body-4-medium",
}))`
  color: ${({ theme }) => theme.colors.primary.$200};
`

export type ContainerProps<C extends ElementType, B extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    label: string
    successMessage: string
    textToCopy: string
    labelProps?: LabelProps
    triggerLabel: string
    buttonProps?: DeprecatedButtonProps<B>
  },
  C,
  C
>

const FullWidthButton = styled(DeprecatedButton)`
  width: 100%;
`
const CLEAR_COPIED_MESSAGE_TIMEOUT = 5000

export const DeprecatedCopyText = <C extends ElementType = "div", B extends ElementType = "button">({
  triggerLabel,
  label,
  successMessage,
  textToCopy,
  labelProps = {},
  buttonProps = {},
  ...rest
}: ContainerProps<C, B>) => {
  const [isCopied, setCopied] = useState(false)

  useEffect(() => {
    if (!isCopied) {
      return
    }

    const timeout = setTimeout(() => {
      setCopied(false)
    }, CLEAR_COPIED_MESSAGE_TIMEOUT)

    return () => {
      clearTimeout(timeout)
    }
  }, [isCopied])

  return (
    <Container {...rest}>
      <Label $variant="body-4-medium" {...labelProps}>
        {label}
      </Label>
      <Pre>
        <code>{textToCopy}</code>
        <span>{isCopied && <SuccessMessage>{successMessage}</SuccessMessage>}</span>
      </Pre>
      <CopyToClipboard
        text={textToCopy}
        onCopy={() => {
          setCopied(true)
        }}
      >
        <FullWidthButton isLoading={false} {...buttonProps}>
          {triggerLabel}
        </FullWidthButton>
      </CopyToClipboard>
    </Container>
  )
}
